import { Box, Icon, Link, Stack, Typography, useTheme } from '@mui/material';
import mixpanel from 'mixpanel-browser';
import { FC, ReactElement, useContext, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import GlobalContext from '../../contexts/GlobalContext';
import LoginContext, { ELoginUiState } from '../../contexts/LoginContext';
import { IconApple, IconFacebook, IconGoogle } from '../../icons/Icons';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import { ELinks, EMixpanelEvent, EThirdPartyProvider } from '../../util/constants';
import { replacePhrase } from '../../util/helpers';
import ScreenHeader from '../ScreenHeader/ScreenHeader';
import ThirdPartyLogin from '../ThirdPartyLogin/ThirdPartyLogin';

type TProvider = {
    name: string;
    icon: ReactElement;
    link: string;
    cta: string;
};

const LoginThirdParty: FC = () => {
    const { language, returnUrlParameter } = useContext(GlobalContext);
    const { setLoginUiState, loginProvider, username } = useContext(LoginContext);
    const theme = useTheme();
    const history = useHistory();
    const provider: TProvider = useMemo(() => {
        switch (loginProvider) {
            case EThirdPartyProvider.apple:
                return {
                    name: language.common.apple,
                    icon: <IconApple />,
                    cta: language.login.loginAppleCTA,
                    link: returnUrlParameter ? `${ELinks.loginApple}&${returnUrlParameter}` : `${ELinks.loginApple}`,
                };
            case EThirdPartyProvider.facebook:
                return {
                    name: language.common.facebook,
                    icon: <IconFacebook />,
                    cta: language.login.loginFacebookCTA,
                    link: returnUrlParameter
                        ? `${ELinks.loginFacebook}&${returnUrlParameter}`
                        : `${ELinks.loginFacebook}`,
                };
            case EThirdPartyProvider.google:
                return {
                    name: language.common.google,
                    icon: <IconGoogle />,
                    cta: language.login.loginGoogleCTA,
                    link: returnUrlParameter ? `${ELinks.loginGoogle}&${returnUrlParameter}` : `${ELinks.loginGoogle}`,
                };
        }
    }, [loginProvider, language, returnUrlParameter]);
    const desc = useMemo(
        () => (
            <>
                {`You originally used ${provider.name} to login to this account under`}{' '}
                <Box component={'span'} typography={'subtitle2'}>
                    {`${username}.`}
                </Box>{' '}
                {`You can proceed with ${provider.name}.`}
            </>
        ),
        [provider, username],
    );
    const thirdPartyDisconnectDesc = useMemo(
        () => replacePhrase(language.login.thirdParty.disconnectDesc, '<provider>', provider.name),
        [provider],
    );

    useEffect(() => {
        mixpanel.track(EMixpanelEvent.viewed, { key: 'consumer_signin_social_prompt' });
    });

    const handleSocialLogin = () => {
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_signin_social_prompt', provider: provider.name });
        window.location.assign(provider.link);
    };

    const handleBack = () => {
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_signin_social_prompt_cancel' });
        setLoginUiState(ELoginUiState.ready);
        history.goBack();
    };

    const handleDisconnect = () => {
        setLoginUiState(ELoginUiState.loginConsumer);
    };

    return (
        <PageLayoutV2 testId="login-consumer-third-party">
            <Link
                variant="body2"
                color={theme.palette.text.primary}
                onClick={handleBack}
                underline="none"
                sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
            >
                <Icon sx={{ mr: 1 }}>arrow_back</Icon>
                {language.common.back}
            </Link>
            <ScreenHeader title={`${provider.name} Account`} desc={desc} />
            <Box sx={{ my: 3 }}>
                <ThirdPartyLogin
                    type="rectangle"
                    label={provider.cta}
                    icon={provider.icon}
                    onClick={handleSocialLogin}
                />
            </Box>
            <Stack sx={{ my: 3 }} direction={'row'} alignItems={'center'} justifyContent={'flex-start'}>
                <Typography variant={'body2'}>{thirdPartyDisconnectDesc}</Typography>
                <Link
                    sx={{ typography: 'subtitle2', ml: 1 }}
                    component={'button'}
                    onClick={handleDisconnect}
                    color={theme.palette.text.primary}
                >
                    {language.login.thirdParty.cta}
                </Link>
            </Stack>
        </PageLayoutV2>
    );
};

export default LoginThirdParty;
